import { sendAnalytics, type PlaycastAnalyticsEvent } from '../../../telemetry/client';
import { getPersistentPlayerId } from '../../../api';
import type { BeaconJoinState } from '../BeaconJoinCoordinator';

const sendBeaconAnalytics = (event: Record<string, unknown>) =>
  sendAnalytics(event as PlaycastAnalyticsEvent);

export interface BeaconTelemetryManagerConfig {
  getState: () => BeaconJoinState;
}

export class BeaconTelemetryManager {
  private config: BeaconTelemetryManagerConfig;
  private sessionStartTime = Date.now();

  constructor(config: BeaconTelemetryManagerConfig) {
    this.config = config;
  }

  private getBaseProperties() {
    const state = this.config.getState();
    return {
      persistentId: getPersistentPlayerId(),
      sessionId: state.sessionId,
      shareCode: state.shareCode,
      flow: 'beacon' as const,
    };
  }

  private getTotalDuration(): number {
    return Date.now() - this.sessionStartTime;
  }

  // --- Page Load ---

  public sendPageViewed(): void {
    sendBeaconAnalytics({
      name: 'Beacon Page Viewed',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  // --- Fast Start ---

  public sendFastStartInitiated(): void {
    sendBeaconAnalytics({
      name: 'Beacon Fast Start Initiated',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendFastStartSucceeded(durationMs: number): void {
    sendBeaconAnalytics({
      name: 'Beacon Fast Start Succeeded',
      role: 'player',
      ...this.getBaseProperties(),
      durationMs,
    });
  }

  public sendFastStartFailed(errorCode: string, errorMessage: string, stage: string): void {
    sendBeaconAnalytics({
      name: 'Beacon Fast Start Failed',
      role: 'player',
      ...this.getBaseProperties(),
      errorCode,
      errorMessage,
      stage,
    });
  }

  // --- WebRTC Connection ---

  public sendBroadcastJoinAttempted(): void {
    sendBeaconAnalytics({
      name: 'Beacon Broadcast Join Attempted',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendBroadcastJoinSucceeded(durationMs: number): void {
    sendBeaconAnalytics({
      name: 'Beacon Broadcast Join Succeeded',
      role: 'player',
      ...this.getBaseProperties(),
      durationMs,
    });
  }

  public sendBroadcastJoinFailed(reason: string, durationMs: number): void {
    sendBeaconAnalytics({
      name: 'Beacon Broadcast Join Failed',
      role: 'player',
      ...this.getBaseProperties(),
      reason,
      durationMs,
    });
  }

  // --- WebRTC Milestones (shared event names, distinguished by flow:'beacon') ---

  public sendPeerIdAcquired(peerId: string): void {
    sendBeaconAnalytics({
      name: 'Peer ID Acquired',
      role: 'player',
      ...this.getBaseProperties(),
      peerId,
    });
  }

  public sendSdpOfferSent(): void {
    sendBeaconAnalytics({
      name: 'SDP Offer Sent',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendSdpAnswerReceived(): void {
    sendBeaconAnalytics({
      name: 'SDP Answer Received',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendIceCandidatesSent(candidateCount: number): void {
    sendBeaconAnalytics({
      name: 'ICE Candidates Sent',
      role: 'player',
      ...this.getBaseProperties(),
      candidateCount,
    });
  }

  public sendIceCheckingStarted(): void {
    sendBeaconAnalytics({
      name: 'ICE Checking Started',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendIceConnected(): void {
    sendBeaconAnalytics({
      name: 'ICE Connected',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendRemoteStreamReceived(): void {
    sendBeaconAnalytics({
      name: 'Remote Stream Received',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  // --- Session Lifecycle ---

  public sendSessionStarted(): void {
    this.sessionStartTime = Date.now();
    sendBeaconAnalytics({
      name: 'Beacon Session Started',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendFirstInputDetected(inputType: string, timeFromJoinMs: number): void {
    sendBeaconAnalytics({
      name: 'Beacon First Input Detected',
      role: 'player',
      ...this.getBaseProperties(),
      inputType,
      timeFromJoinMs,
    });
  }

  public sendPlayerDisconnected(reason?: string): void {
    sendBeaconAnalytics({
      name: 'Beacon Player Disconnected',
      role: 'player',
      ...this.getBaseProperties(),
      reason,
      sessionDurationMs: this.getTotalDuration(),
    });
  }

  public sendPlayerReconnecting(attempt: number): void {
    sendBeaconAnalytics({
      name: 'Beacon Player Reconnecting',
      role: 'player',
      ...this.getBaseProperties(),
      attempt,
    });
  }

  public sendSessionEnded(reason: string): void {
    sendBeaconAnalytics({
      name: 'Beacon Session Ended',
      role: 'player',
      ...this.getBaseProperties(),
      reason,
      sessionDurationMs: this.getTotalDuration(),
    });
  }

  public sendNotFound(): void {
    sendBeaconAnalytics({
      name: 'Beacon Not Found',
      role: 'player',
      ...this.getBaseProperties(),
    });
  }

  public sendTabVisibilityChanged(visible: boolean): void {
    sendBeaconAnalytics({
      name: 'Beacon Tab Visibility Changed',
      role: 'player',
      ...this.getBaseProperties(),
      visible,
    });
  }

  public cleanup(): void {
    // No timers or subscriptions to clean up
  }
}
